import React, { useState, useRef, useCallback } from 'react';

const FAQCard = ({ question, answer, index, category, relatedLinks }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const cardRef = useRef(null);
  const animFrameRef = useRef(null);

  const categoryColors = {
    konto: '#3B6FD4',
    fakturering: '#E8521A',
    bomkalkulator: '#2A9D8F',
    teknisk: '#8B5CF6',
  };

  const baseColor = categoryColors?.[category] || '#3B6FD4';
  // Transition accent color: base → orange on hover, teal when open
  const accentColor = isOpen ? '#2EC4A0' : isHovered ? '#F5A623' : baseColor;

  const handleMouseMove = useCallback((e) => {
    if (!cardRef?.current || isOpen) return;
    if (animFrameRef?.current) cancelAnimationFrame(animFrameRef?.current);
    animFrameRef.current = requestAnimationFrame(() => {
      const rect = cardRef?.current?.getBoundingClientRect();
      if (!rect) return;
      const cx = rect?.left + rect?.width / 2;
      const cy = rect?.top + rect?.height / 2;
      const dx = (e?.clientX - cx) / (rect?.width / 2);
      const dy = (e?.clientY - cy) / (rect?.height / 2);
      setTilt({ x: dy * -3, y: dx * 3 });
    });
  }, [isOpen]);

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => {
    setIsHovered(false);
    setTilt({ x: 0, y: 0 });
    if (animFrameRef?.current) cancelAnimationFrame(animFrameRef?.current);
  };

  const liftY = isOpen ? -4 : isHovered ? -3 : 0;
  const scaleVal = isOpen ? 1.005 : isHovered ? 1.008 : 1;
  const transform = `translateY(${liftY}px) scale(${scaleVal}) rotateX(${isHovered && !isOpen ? tilt?.x : 0}deg) rotateY(${isHovered && !isOpen ? tilt?.y : 0}deg)`;

  return (
    <div
      ref={cardRef}
      style={{
        background: isOpen ? 'rgba(255,255,255,0.04)' : isHovered ? 'rgba(255,255,255,0.025)' : 'transparent',
        border: `1px solid ${isOpen ? accentColor + '40' : isHovered ? accentColor + '28' : 'rgba(255,255,255,0.06)'}`,
        borderLeft: `3px solid ${accentColor}`,
        transform,
        transformStyle: 'preserve-3d',
        transition: 'transform 360ms cubic-bezier(0.34, 1.56, 0.64, 1), background 280ms ease, border-color 280ms ease, border-left-color 280ms ease, box-shadow 280ms ease',
        boxShadow: isOpen
          ? `0 8px 32px rgba(0,0,0,0.35), 0 0 0 1px ${accentColor}20`
          : isHovered
          ? `0 4px 20px rgba(0,0,0,0.25), 0 0 0 1px ${accentColor}15`
          : 'none',
        marginBottom: '2px',
        willChange: 'transform'
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
    >
      {/* Question header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%', padding: '28px 32px',
          background: 'none', border: 'none',
          cursor: 'pointer', textAlign: 'left',
          display: 'flex', alignItems: 'flex-start', gap: '20px'
        }}
      >
        {/* Index number */}
        <div style={{
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: '0.6875rem', fontWeight: 700,
          color: accentColor, letterSpacing: '0.1em',
          minWidth: '32px', paddingTop: '3px',
          opacity: 0.8,
          transition: 'color 280ms ease'
        }}>{String(index + 1)?.padStart(2, '0')}</div>
        {/* Question text */}
        <div style={{ flex: 1 }}>
          <div style={{
            fontFamily: 'Syne, sans-serif',
            fontSize: '1.0625rem', fontWeight: 700,
            color: '#EEF0F2', letterSpacing: '-0.01em',
            lineHeight: 1.4
          }}>{question}</div>
        </div>
        {/* Toggle indicator */}
        <div style={{
          width: '32px', height: '32px',
          border: `1px solid ${isOpen || isHovered ? accentColor : 'rgba(255,255,255,0.1)'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          transition: 'all 280ms cubic-bezier(0.34, 1.56, 0.64, 1)',
          background: isOpen ? accentColor + '20' : isHovered ? accentColor + '10' : 'transparent',
          transform: isOpen ? 'scale(1.1)' : 'scale(1)'
        }}>
          <span style={{
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: '1.1rem', color: isOpen || isHovered ? accentColor : 'rgba(138,149,163,0.5)',
            lineHeight: 1, display: 'block',
            transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
            transition: 'transform 280ms cubic-bezier(0.34, 1.56, 0.64, 1), color 280ms ease'
          }}>+</span>
        </div>
      </button>
      {/* Answer content — spring expand */}
      <div style={{
        overflow: 'hidden',
        maxHeight: isOpen ? '600px' : '0px',
        opacity: isOpen ? 1 : 0,
        transition: isOpen
          ? 'max-height 420ms cubic-bezier(0.34, 1.56, 0.64, 1), opacity 300ms ease 80ms'
          : 'max-height 300ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms ease'
      }}>
        <div style={{ padding: '0 32px 32px 84px' }}>
          {/* Accent line */}
          <div style={{
            width: '40px', height: '2px',
            background: accentColor,
            marginBottom: '20px',
            transition: 'background 280ms ease'
          }} />
          {/* Answer text */}
          <div style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.9375rem', color: 'rgba(238,240,242,0.75)',
            lineHeight: 1.8, marginBottom: relatedLinks?.length ? '24px' : 0
          }}
            dangerouslySetInnerHTML={{ __html: answer }}
          />
          {/* Related links */}
          {relatedLinks?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '20px' }}>
              <span style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: '0.6875rem', color: 'rgba(138,149,163,0.5)',
                letterSpacing: '0.1em', textTransform: 'uppercase',
                alignSelf: 'center'
              }}>SE OGSÅ →</span>
              {relatedLinks?.map((link, i) => (
                <a key={i} href={link?.href}
                  style={{
                    fontFamily: 'Inter, sans-serif', fontSize: '0.8125rem',
                    color: accentColor, textDecoration: 'none',
                    border: `1px solid ${accentColor}33`,
                    padding: '5px 12px',
                    transition: 'all 200ms cubic-bezier(0.34, 1.56, 0.64, 1)'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = accentColor + '20'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >{link?.label}</a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FAQCard;
